import React from 'react';
import { FleetInvitationOut, resolveFleetInvitation } from '../../services/fleet';

function formatDate(value: string | null | undefined) {
  if (!value) return 'Unknown';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

// Deep-link scheme for the DailyMove driver app.
// The app should handle  dailymove://fleet-invitations?token=<token>
const APP_SCHEME = 'dailymove';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.dailymove.driver';
const APP_STORE_URL = 'https://apps.apple.com/app/dailymove-driver';

export function FleetInvitationAcceptance() {
  const token = new URLSearchParams(window.location.search).get('token') ?? '';
  const [invitation, setInvitation] = React.useState<FleetInvitationOut | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState('');

  React.useEffect(() => {
    if (!token) {
      setLoadError('No invitation token found in the link.');
      setIsLoading(false);
      return;
    }
    resolveFleetInvitation(token)
      .then(setInvitation)
      .catch((error) => setLoadError(error instanceof Error ? error.message : 'Invitation not found or has already been responded to.'))
      .finally(() => setIsLoading(false));
  }, [token]);

  const openInApp = () => {
    window.location.href = `${APP_SCHEME}://fleet-invitations?token=${encodeURIComponent(token)}`;
  };

  return (
    <div className="fleet-invitation-page">
      <div className="fleet-invitation-card">
        <div className="fleet-invitation-brand">
          <FleetIcon />
          <span>DailyMove Fleet</span>
        </div>

        {isLoading ? (
          <div className="fleet-invitation-state">
            <h2>Loading invitation…</h2>
            <p>Please wait while we look up your invitation.</p>
          </div>
        ) : loadError ? (
          <div className="fleet-invitation-state error">
            <NotFoundIcon />
            <h2>Invitation not found</h2>
            <p>{loadError}</p>
          </div>
        ) : invitation ? (
          <>
            <div className="fleet-invitation-header">
              <span className="fleet-invitation-icon"><FleetIcon /></span>
              <div>
                <h2>Fleet invitation</h2>
                <span className={`pill ${invitation.status === 'pending' ? 'info' : invitation.status === 'accepted' ? 'success' : 'muted'}`}>{invitation.status}</span>
              </div>
            </div>

            <dl className="fleet-invitation-details">
              <div><dt>Invitation ID</dt><dd className="kyc-document">{invitation.id}</dd></div>
              <div><dt>Fleet</dt><dd>{invitation.fleet_id ?? 'General pool'}</dd></div>
              <div><dt>Sent</dt><dd>{formatDate(invitation.created_at)}</dd></div>
              {invitation.responded_at ? <div><dt>Responded</dt><dd>{formatDate(invitation.responded_at)}</dd></div> : null}
            </dl>

            {invitation.status !== 'pending' ? (
              <div className="fleet-invitation-state muted">
                <CheckIcon />
                <p>This invitation has already been <strong>{invitation.status}</strong>.</p>
              </div>
            ) : (
              <>
                <div className="fleet-invitation-app-prompt">
                  <PhoneIcon />
                  <div>
                    <strong>Open in the DailyMove driver app</strong>
                    <p>Accept or decline this invitation from the DailyMove driver app on your phone.</p>
                  </div>
                </div>
                <div className="fleet-invitation-actions">
                  <button className="btn btn-primary fleet-invitation-open-btn" type="button" onClick={openInApp}>
                    <PhoneIcon /> Open in DailyMove app
                  </button>
                </div>
                <div className="fleet-invitation-store-links">
                  <span>Don't have the app?</span>
                  <a href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer">Google Play</a>
                  <span>·</span>
                  <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer">App Store</a>
                </div>
              </>
            )}
          </>
        ) : null}
      </div>
    </div>
  );
}

function FleetIcon() {
  return <svg width="28" height="28" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 6h12v10H3zM15 10h4l2 3v3h-6z" /><circle cx="7" cy="18" r="2" /><circle cx="18" cy="18" r="2" /></svg>;
}
function CheckIcon() {
  return <svg width="40" height="40" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><path d="M8 12l3 3 5-6" /></svg>;
}
function NotFoundIcon() {
  return <svg width="40" height="40" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 3L2.5 20h19L12 3z" /><path d="M12 9v5M12 17h.01" /></svg>;
}
function PhoneIcon() {
  return <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2"><rect x="5" y="2" width="14" height="20" rx="2" /><circle cx="12" cy="18" r="1" /></svg>;
}
